import { useContent } from "@/content/ContentProvider";
import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ArrowUp, ArrowUpRight, Grip, Lock, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Toaster } from "@/components/ui/sonner";
import { PillButton, Roll } from "./Kit";
import Cursor from "./Cursor";
import { NomusLogo } from "./BrandMarks";
import { Reveal } from "./Reveal";
import Consent from "./Consent";
import { CONSENT_OPEN } from "@/lib/track";
export function Logo({ className = "" }) {
  return (
    <span className={`group/logo inline-flex items-center ${className}`}>
      <NomusLogo className="h-[15px] w-auto text-[#050606]" />
    </span>
  );
}
// `bare` is a client's private page: the mark, and nothing that sells.
function Header({ onMenu, hidden, scrolled, bare }) {
  const { text: cmsText, nextLayout } = useContent();
  if (bare)
    return (
      <header className="pointer-events-none fixed left-0 top-0 z-40 p-3 sm:p-5 print:hidden">
        <Link
          to="/"
          data-testid="header-logo"
          aria-label={cmsText("Layout.Header.nomus-home.b6038f")}
          className={`pointer-events-auto flex h-14 items-center rounded-[14px] bg-white/90 px-5 ring-1 ring-black/5 backdrop-blur-md transition-shadow duration-500 ${scrolled ? "shadow-[0_8px_30px_-12px_rgba(0,0,0,0.18)]" : ""}`}
        >
          <Logo />
        </Link>
      </header>
    );
  return (
    <header className="pointer-events-none fixed print:hidden left-0 top-0 z-40 flex w-full items-start justify-between p-3 sm:p-5">
      <div
        className={`pointer-events-auto flex h-14 items-center rounded-[14px] bg-white/90 ring-1 ring-black/5 backdrop-blur-md transition-shadow duration-500 ${scrolled ? "shadow-[0_8px_30px_-12px_rgba(0,0,0,0.18)]" : ""}`}
      >
        <Link
          to="/"
          className="flex h-full items-center px-5"
          data-testid="header-logo"
          aria-label={cmsText("Layout.Header.nomus-home.b6038f")}
        >
          <Logo />
        </Link>
        <span className="h-6 w-px bg-black/10" />
        <button
          type="button"
          onClick={onMenu}
          data-testid="menu-open-btn"
          className="group/roll flex h-full items-center gap-2 px-5 text-[15px] font-medium tracking-[-0.01em]"
        >
          <Grip
            size={16}
            strokeWidth={1.6}
            className="transition-transform duration-500 group-hover/roll:rotate-90"
          />
          <Roll>{cmsText("Layout.Header.menu.57f5f5")}</Roll>
        </button>
      </div>
      <div
        className={`pointer-events-auto hidden transition-transform duration-500 ease-[cubic-bezier(.76,0,.24,1)] sm:block ${hidden ? "-translate-y-[160%]" : "translate-y-0"}`}
      >
        <Link
          to={nextLayout ? "/scorecard" : "/contact"}
          data-testid="header-cta"
          className="group/roll nm-pill-btn flex h-14 items-center gap-2 rounded-[14px] bg-black px-6 text-[15px] font-medium tracking-[-0.01em] text-white hover:bg-[#262626]"
        >
          <Roll>{cmsText("Layout.Header.discuss-your-workflow.184fa5")}</Roll>
          <ArrowUpRight
            size={16}
            className="transition-transform duration-500 group-hover/roll:translate-x-0.5 group-hover/roll:-translate-y-0.5"
          />
        </Link>
      </div>
    </header>
  );
}
function MenuSheet({ open, setOpen }) {
  const { text: cmsText, NAV, CONTACT_EMAIL } = useContent();
  const { pathname } = useLocation();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="left"
        data-testid="menu-sheet"
        className="w-full max-w-none border-0 bg-[#f7f7f7] p-3 sm:max-w-[540px] sm:p-5 [&>button]:hidden"
      >
        <SheetTitle className="sr-only">
          {cmsText("Layout.MenuSheet.menu.57f5f5")}
        </SheetTitle>
        <SheetDescription className="sr-only">
          {cmsText("Layout.MenuSheet.site-navigation.880af1")}
        </SheetDescription>
        <div className="flex h-full flex-col overflow-y-auto">
          <div className="flex h-14 shrink-0 items-center justify-between rounded-[14px] bg-white pl-5 pr-2">
            <Logo />
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="menu-close-btn"
              className="group/roll flex h-10 items-center gap-2 rounded-[10px] px-4 text-[15px] font-medium transition-colors hover:bg-[#f7f7f7]"
            >
              <X
                size={16}
                className="transition-transform duration-500 group-hover/roll:rotate-90"
              />{" "}
              <Roll>{cmsText("Layout.MenuSheet.close.bbfa77")}</Roll>
            </button>
          </div>
          <nav
            className="mt-3 flex flex-1 flex-col gap-2"
            aria-label={cmsText("Layout.MenuSheet.main.62bce9")}
          >
            {NAV.map((n, i) => {
              const active = pathname === n.to;
              return (
                <Link
                  key={n.label}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  aria-current={active ? "page" : undefined}
                  data-testid={`menu-link-${n.label.toLowerCase()}`}
                  style={{
                    animationDelay: `${120 + i * 70}ms`,
                  }}
                  className="nm-menu-item nm-display group flex min-h-[64px] flex-1 items-center justify-between rounded-[20px] bg-white px-6 text-[34px] leading-none tracking-[-0.03em] transition-colors duration-300 hover:bg-black hover:text-white sm:text-[46px]"
                >
                  <span className="flex items-center gap-3">
                    {active && (
                      <span className="h-2 w-2 rounded-full bg-current" />
                    )}
                    {n.label}
                  </span>
                  <span className="flex items-center gap-3 text-[12px] tracking-normal">
                    <ArrowUpRight
                      size={18}
                      className="-translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
                    />
                    <span className="tabular-nums opacity-40">0{i + 1}</span>
                  </span>
                </Link>
              );
            })}
          </nav>
          <Link
            to="/client"
            onClick={() => setOpen(false)}
            data-testid="menu-link-client"
            style={{
              animationDelay: "560ms",
            }}
            className="nm-menu-item group mt-2 flex h-14 shrink-0 items-center justify-between rounded-[20px] bg-white px-6 text-[15px] font-medium transition-colors duration-300 hover:bg-black hover:text-white"
          >
            <span className="flex items-center gap-2.5">
              <Lock size={14} /> Client sign in
            </span>
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </Link>
          <div
            className="nm-menu-item mt-2 shrink-0 rounded-[20px] bg-white p-6 text-[15px]"
            style={{
              animationDelay: "620ms",
            }}
          >
            <p className="opacity-50">
              {cmsText(
                "Layout.MenuSheet.tell-us-what-keeps-getting-stuck.eed7b7",
              )}
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="nm-link mt-1 inline-block text-[22px] tracking-[-0.02em]"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
function Footer() {
  const { text: cmsText, NAV, CONTACT_EMAIL, nextLayout } = useContent();
  return (
    <footer
      className="bg-[#f7f7f7] px-5 pt-20 lg:pt-28"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="nm-title text-[30px] leading-[1.05] sm:text-[44px]">
              {cmsText("Layout.Footer.make-ai-normal-.916c06")}
            </p>
            <div className="mt-8">
              <PillButton to={nextLayout ? "/book" : "/contact"}>
                {cmsText("Layout.Footer.discuss-your-workflow.184fa5")}
              </PillButton>
            </div>
            <p className="mt-8 max-w-[400px] text-[16px] leading-[1.4] opacity-60">
              {cmsText(
                "Layout.Footer.connected-internal-tools-for-your-team-and-ag.654c7b",
              )}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8 text-[15px] font-medium tracking-[-0.01em] sm:grid-cols-3 lg:col-span-6 lg:col-start-7">
            <div className="flex flex-col gap-2.5">
              <span className="mb-2 text-[12px] font-normal opacity-40">
                {cmsText("Layout.Footer.index.c2df9b")}
              </span>
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} className="nm-link w-fit">
                  {n.label}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="mb-2 text-[12px] font-normal opacity-40">
                {cmsText("Layout.Footer.reach-us.086f6a")}
              </span>
              <a href={`mailto:${CONTACT_EMAIL}`} className="nm-link w-fit">
                {CONTACT_EMAIL}
              </a>
              <Link to="/contact" className="nm-link w-fit">
                {cmsText("Layout.Footer.describe-the-work.60fef0")}
              </Link>
              <Link
                to="/client"
                className="nm-link w-fit"
                data-testid="footer-client"
              >
                Client sign in
              </Link>
            </div>
            <div className="col-span-2 flex flex-col gap-2.5 sm:col-span-1">
              <span className="mb-2 text-[12px] font-normal opacity-40">
                {cmsText("Layout.Footer.working-together.6d2444")}
              </span>
              <span>{cmsText("Layout.Footer.end-to-end-delivery.282b39")}</span>
              <span>{cmsText("Layout.Footer.one-workflow-first.2c9dbb")}</span>
              <span>{cmsText("Layout.Footer.scope-before-build.2e3093")}</span>
              <span>{cmsText("Layout.Footer.defined-support.7e0441")}</span>
            </div>
          </div>
        </div>

        <Reveal
          className="group/logo mt-16 select-none px-[2%] pb-6 sm:mt-20 sm:pb-8"
          aria-hidden="true"
          data-testid="footer-logo"
        >
          <NomusLogo className="block h-auto w-full text-[#050606]" />
        </Reveal>
      </div>
      <div className="relative z-10 mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-x-8 gap-y-5 border-t border-black/10 py-6 text-[12px] md:flex-row md:items-center">
        <span className="opacity-50">
          {cmsText(
            "Layout.Footer.-2026-nomus-connected-tools-everyday-work-.9b4f22",
          )}
        </span>
        <div className="flex flex-wrap items-center gap-5">
          <Link to="/privacy" className="nm-link opacity-50">
            Privacy
          </Link>
          <button
            type="button"
            data-testid="cookie-settings"
            onClick={() => window.dispatchEvent(new Event(CONSENT_OPEN))}
            className="nm-link opacity-50"
          >
            Cookie settings
          </button>
          <button
            type="button"
            data-testid="back-to-top"
            onClick={() =>
              window.scrollTo({
                top: 0,
                behavior: "smooth",
              })
            }
            className="group/roll flex h-9 items-center gap-2 rounded-full bg-white px-4 font-medium transition-colors hover:bg-black hover:text-white"
          >
            <Roll>{cmsText("Layout.Footer.back-to-top.d9c883")}</Roll>{" "}
            <ArrowUp size={12} />
          </button>
        </div>
      </div>
    </footer>
  );
}
export default function Layout() {
  const {
    text: cmsText,
    STATIC: siteStatic,
    categories,
    articles,
    articleCategories,
    pages,
    nextLayout,
  } = useContent();
  const TITLES = siteStatic["Layout"].TITLES;
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const bar = useRef(null);
  const { pathname, hash } = useLocation();
  // Pages that end in a full-width stage meet the footer directly, with no white band between.
  const bare =
    pathname.startsWith("/a/") ||
    pathname.startsWith("/p/") ||
    pathname === "/client" ||
    pathname === "/audit/preview";
  const flush =
    pathname.startsWith("/scorecard") ||
    bare ||
    (nextLayout && pathname === "/");
  useEffect(() => {
    if (hash) {
      const t = setTimeout(
        () =>
          document.getElementById(hash.slice(1))?.scrollIntoView({
            behavior: "smooth",
          }),
        120,
      );
      return () => clearTimeout(t);
    }
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
    return undefined;
  }, [pathname, hash]);
  useEffect(() => {
    const seg = pathname.split("/")[1] || "";
    const slug = pathname.split("/").at(-1);
    const records = pathname.startsWith("/for/")
      ? categories
      : pathname.startsWith("/insights/category/")
        ? articleCategories
        : pathname.startsWith("/insights/")
          ? articles
          : pathname.startsWith("/pages/")
            ? pages
            : [];
    const record = records.find((x) => x.slug === slug);
    document.title = record
      ? record.seo?.title || record.title + " | Nomus"
      : TITLES[seg] ||
        (seg === "scorecard"
          ? "Find the job worth fixing | Nomus"
          : seg === "privacy"
            ? "Privacy | Nomus"
            : seg === "a"
              ? "Your Audit | Nomus"
              : seg === "p"
                ? "Private | Nomus"
              : seg === "client"
                ? "Client sign in | Nomus"
                : seg === "book"
                  ? "Book the Audit | Nomus"
                  : seg === "paid"
                    ? "Paid | Nomus"
                    : seg === "pricing"
                      ? "Pricing | Nomus"
                      : seg === "booked"
                        ? "Call booked | Nomus"
                        : seg === "audit"
                          ? pathname.endsWith("/sample")
                            ? "A sample Audit | Nomus"
                            : "The Audit, day by day | Nomus"
                          : "Nomus");
    const pageUrl = new URL(pathname, "https://nomus.work").href;
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute("href", pageUrl);
    document
      .querySelector('meta[property="og:url"]')
      ?.setAttribute("content", pageUrl);
  }, [pathname, TITLES, categories, articles, articleCategories, pages]);
  useEffect(() => {
    let last = window.scrollY;
    const on = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      if (y > last + 6 && y > 240) setHidden(true);
      else if (y < last - 6) setHidden(false);
      last = y;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (bar.current)
        bar.current.style.transform = `scaleX(${max > 0 ? y / max : 0})`;
    };
    on();
    window.addEventListener("scroll", on, {
      passive: true,
    });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <div id="top" className="min-h-screen bg-white text-black">
      <a href="#main" className="nm-skip">
        {cmsText("Layout.Layout.skip-to-main-content.be504f")}
      </a>
      <div
        ref={bar}
        aria-hidden="true"
        className="fixed left-0 top-0 z-[45] h-[2px] w-full origin-left scale-x-0 bg-black"
        data-testid="scroll-progress"
      />
      <Header
        onMenu={() => setOpen(true)}
        hidden={hidden}
        scrolled={scrolled}
        bare={bare}
      />
      <MenuSheet open={open} setOpen={setOpen} />
      <main
        id="main"
        tabIndex={-1}
        key={pathname}
        className={`animate-in fade-in duration-700 outline-none ${flush ? "" : "pb-20 lg:pb-28"}`}
      >
        <Outlet />
      </main>
      {bare ? (
        <footer
          className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-black/10 px-5 py-6 text-[12px] print:hidden"
          data-testid="room-footer"
        >
          <span className="opacity-50">Nomus is Goodvibe OÜ, Tallinn</span>
          <Link to="/privacy" className="nm-link opacity-50">
            Privacy
          </Link>
          <a href="mailto:hello@nomus.work" className="nm-link opacity-50">
            hello@nomus.work
          </a>
        </footer>
      ) : (
        <Footer />
      )}
      {!bare && <Consent />}
      <Cursor />
      <div
        aria-hidden="true"
        className="nm-grain pointer-events-none fixed inset-0 z-[90] opacity-[0.028]"
      />
      <Toaster
        position="bottom-right"
        toastOptions={{
          classNames: {
            toast:
              "!rounded-[18px] !border-0 !bg-black !px-5 !py-4 !text-white !shadow-2xl",
            title: "!font-medium",
            description: "!text-white/60",
          },
        }}
      />
    </div>
  );
}
